import { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import EditorTexto, { htmlVacio } from "../kit/EditorTexto";
import { useAreas, useCreateTema, useDocentes, useUpdateTema } from "../../queries/useTemas";
import { useAuthStore } from "../../stores/authStore";
import { AreaInvestigacion, DocenteOpcion, TemaApp } from "../../types/tema";
import { mensajeDeError } from "../../utils/errores";

/**
 * Formulario de alta/edición de un tema del catálogo (guía/coguía o
 * coordinador). Sin `tema` crea uno nuevo (nace disponible); con `tema`
 * precarga sus valores y guarda los cambios. El coguía es interno (un docente)
 * o externo (texto libre), nunca ambos.
 *
 * El selector de **profesor guía** sale en dos casos y solo en esos: cuando un
 * estudiante propone un tema (elige con quién quiere trabajar) y cuando la
 * coordinación edita uno, para traspasarlo a otra persona. Un docente no cambia
 * el guía de su propio tema —eso es entregárselo a otro— pero sí el coguía.
 * Si el tema ya está asignado, el cambio baja también al proceso en curso del
 * estudiante (lo hace el backend).
 */
export default function CrearTemaDialog({ open, onClose, tema }: { open: boolean; onClose: () => void; tema?: TemaApp }) {
  const navigate = useNavigate();
  const editando = !!tema;
  const crear = useCreateTema();
  const actualizar = useUpdateTema();
  const m = editando ? actualizar : crear;
  const { data: areas = [] } = useAreas();
  const { user } = useAuthStore();
  // Estudiante puro (no docente/coordinador): propone el tema y elige guía.
  const esProponente = !editando && !!user?.es_estudiante && !user?.es_coordinador_global && !user?.es_docente;
  // Traspasar el tema a otro profesor: solo la coordinación, y solo editando.
  const puedeTraspasar = editando && !!user?.es_coordinador_global;
  const eligeGuia = esProponente || puedeTraspasar;

  const [titulo, setTitulo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [tipos, setTipos] = useState({ is_memoria: true, is_doble_titulacion: false, is_practica: false });
  const [areasSel, setAreasSel] = useState<AreaInvestigacion[]>([]);
  const [coguiaInterno, setCoguiaInterno] = useState<DocenteOpcion | null>(null);
  const [docenteQuery, setDocenteQuery] = useState("");
  const [coguiaExterno, setCoguiaExterno] = useState("");
  const [coguiaExternoEmail, setCoguiaExternoEmail] = useState("");
  const [guia, setGuia] = useState<DocenteOpcion | null>(null);
  const [guiaQuery, setGuiaQuery] = useState("");

  const { data: docentes = [], isFetching: buscandoDocentes } = useDocentes(docenteQuery, open);
  const { data: guiaDocentes = [], isFetching: buscandoGuia } = useDocentes(guiaQuery, open && eligeGuia);

  // Al abrir en modo edición, precarga los valores del tema.
  useEffect(() => {
    if (!open || !tema) return;
    setTitulo(tema.titulo);
    setDescripcion(tema.descripcion || "");
    setTipos({ is_memoria: tema.is_memoria, is_doble_titulacion: tema.is_doble_titulacion, is_practica: tema.is_practica });
    setCoguiaInterno(tema.coguia_id ? { id: tema.coguia_id, nombre: tema.coguia || "" } : null);
    setGuia(tema.guia_id ? { id: tema.guia_id, nombre: tema.guia || "" } : null);
    setCoguiaExterno(tema.coguia_es_externo ? tema.coguia || "" : "");
    setCoguiaExternoEmail(tema.coguia_es_externo ? tema.coguia_email || "" : "");
  }, [open, tema]);

  // Las áreas seleccionadas necesitan la lista de áreas cargada.
  useEffect(() => {
    if (open && tema) setAreasSel(areas.filter((a) => tema.areas_ids?.includes(a.id)));
  }, [open, tema, areas]);

  const hayExterno = coguiaExterno.trim().length > 0 || coguiaExternoEmail.trim().length > 0;
  const tituloVacio = titulo.trim().length === 0;

  const reset = () => {
    setTitulo("");
    setDescripcion("");
    setTipos({ is_memoria: true, is_doble_titulacion: false, is_practica: false });
    setAreasSel([]);
    setCoguiaInterno(null);
    setDocenteQuery("");
    setCoguiaExterno("");
    setCoguiaExternoEmail("");
    setGuia(null);
    setGuiaQuery("");
    m.reset();
  };

  const cerrar = () => {
    if (m.isPending) return;
    reset();
    onClose();
  };

  const enviar = () => {
    if (tituloVacio || (eligeGuia && !guia)) return;
    const input = {
      titulo: titulo.trim(),
      descripcion: htmlVacio(descripcion) ? "" : descripcion.trim(),
      ...tipos,
      areas: areasSel.map((a) => a.id),
      coguia: coguiaInterno?.id ?? null,
      coguia_externo: coguiaInterno ? "" : coguiaExterno.trim(),
      coguia_externo_email: coguiaInterno ? "" : coguiaExternoEmail.trim(),
      // El guía elegido: al proponer es el creador del tema; al editar (coordinación)
      // el traspaso. Sin selector no se manda, para no tocar el que ya tiene.
      ...(eligeGuia ? { guia: guia?.id ?? null } : {}),
    };
    if (editando) {
      actualizar.mutate(
        { id: tema.id, input },
        {
          onSuccess: () => {
            reset();
            onClose();
          },
        },
      );
    } else {
      crear.mutate(input, {
        onSuccess: (creado) => {
          reset();
          onClose();
          navigate(`/app/temas/${creado.id}`);
        },
      });
    }
  };

  const toggleTipo = (campo: keyof typeof tipos) => setTipos((t) => ({ ...t, [campo]: !t[campo] }));

  return (
    <Dialog open={open} onClose={cerrar} maxWidth="sm" fullWidth>
      <DialogTitle>{editando ? "Editar tema" : esProponente ? "Proponer tema" : "Crear tema"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          {m.isError && <Alert severity="error">{mensajeDeError(m.error, "No se pudo guardar el tema.")}</Alert>}
          {esProponente && (
            <Alert severity="info">
              Propones un tema y eliges tu profesor guía. Queda pendiente hasta que ese profesor lo acepta.
            </Alert>
          )}

          <TextField
            label="Título"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            required
            fullWidth
            autoFocus
            disabled={m.isPending}
          />

          <EditorTexto
            label="Descripción"
            value={descripcion}
            onChange={setDescripcion}
            placeholder="Qué se hará, con qué herramientas y qué se espera del estudiante…"
            disabled={m.isPending}
          />

          {eligeGuia && (
            <Autocomplete
              options={guiaDocentes}
              getOptionLabel={(d) => d.nombre}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={guia}
              onChange={(_, value) => setGuia(value)}
              onInputChange={(_, value, reason) => reason === "input" && setGuiaQuery(value)}
              loading={buscandoGuia}
              disabled={m.isPending}
              filterOptions={(x) => x}
              noOptionsText={guiaQuery ? "Sin resultados" : "Escribe para buscar un profesor"}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Profesor guía"
                  required
                  placeholder={esProponente ? "Con quién harás la tesis…" : "Buscar profesor…"}
                  helperText={puedeTraspasar ? "Cambiarlo traspasa el tema, y con él el proceso en curso del estudiante." : undefined}
                />
              )}
            />
          )}

          <Autocomplete
            multiple
            options={areas}
            getOptionLabel={(a) => a.nombre}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            value={areasSel}
            onChange={(_, value) => setAreasSel(value)}
            disabled={m.isPending}
            renderInput={(params) => <TextField {...params} label="Áreas" placeholder="Categorías del tema" />}
          />

          <Stack spacing={0.5}>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
              Tipos de trabajo que admite
            </Typography>
            <FormGroup row>
              <FormControlLabel
                control={<Checkbox checked={tipos.is_memoria} onChange={() => toggleTipo("is_memoria")} />}
                label="Memoria"
                disabled={m.isPending}
              />
              <FormControlLabel
                control={<Checkbox checked={tipos.is_doble_titulacion} onChange={() => toggleTipo("is_doble_titulacion")} />}
                label="Doble titulación"
                disabled={m.isPending}
              />
              <FormControlLabel
                control={<Checkbox checked={tipos.is_practica} onChange={() => toggleTipo("is_practica")} />}
                label="Práctica de título"
                disabled={m.isPending}
              />
            </FormGroup>
          </Stack>

          <Stack spacing={1.5}>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
              Coguía (opcional) — interno o externo
            </Typography>
            <Autocomplete
              options={docentes}
              getOptionLabel={(d) => d.nombre}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={coguiaInterno}
              onChange={(_, value) => setCoguiaInterno(value)}
              onInputChange={(_, value, reason) => reason === "input" && setDocenteQuery(value)}
              loading={buscandoDocentes}
              disabled={m.isPending || hayExterno}
              filterOptions={(x) => x}
              noOptionsText={docenteQuery ? "Sin resultados" : "Escribe para buscar un docente"}
              renderInput={(params) => <TextField {...params} label="Coguía interno" placeholder="Buscar docente…" />}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField
                label="Coguía externo"
                value={coguiaExterno}
                onChange={(e) => setCoguiaExterno(e.target.value)}
                fullWidth
                disabled={m.isPending || coguiaInterno !== null}
              />
              <TextField
                label="Email del coguía externo"
                type="email"
                value={coguiaExternoEmail}
                onChange={(e) => setCoguiaExternoEmail(e.target.value)}
                fullWidth
                disabled={m.isPending || coguiaInterno !== null}
              />
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={cerrar} disabled={m.isPending}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={enviar} disabled={m.isPending || tituloVacio || (eligeGuia && !guia)}>
          {m.isPending ? "Guardando…" : editando ? "Guardar cambios" : esProponente ? "Proponer tema" : "Crear tema"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
